import { Component, computed, inject, input, LOCALE_ID, output, signal } from '@angular/core';
import { HasScopeDirective } from '../../../../../../services/scope/has-scope.directive';
import { SCOPES } from '../../../../../../services/scope/scope-constants';
import { FilterSelectComponent } from '../../../../../../shared/ui/shared-filters/filter-select-component/filter-select-component';
import { FilterProductSearch } from '../../../../../../shared/ui/shared-filters/filter-product-search/filter-product-search';
import { OffersService } from '../../../../../../services/offers/offers.service';
import { finalize } from 'rxjs';
import { FilterMultiSelectComponent } from '../../../../../../shared/ui/shared-filters/filter-multi-select-component/filter-multi-select-component';
import { FilterDateComponent } from '../../../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { OfferFiltersApi } from '../../../../../../pages/admin-dashboard/promotions/offers-management/offers.model';
import { FilterItemSearch } from '../../../../../../shared/ui/shared-filters/filter-item-search/filter-item-search';

@Component({
  selector: 'app-filters',
  imports: [FilterProductSearch, FilterMultiSelectComponent, FilterDateComponent, FilterItemSearch],
  templateUrl: './filters.html',
  styleUrl: './filters.scss',
})
export class Filters {
  private offersService = inject(OffersService);
  private locale = inject(LOCALE_ID);
  isArabic = computed(() => this.locale.startsWith('ar'));
  readonly SCOPES = SCOPES;
  loading = signal(false);
  errorSearching = signal('');
  selectedItem = signal<{ id: number; titleAr: string; titleEn: string } | null>(null);
  // STATUS FILTER
  selectedStatuses = signal<string[]>([]);
  showStatusDropdown = signal(false);
  // OFFER TYPE FILTER
  selectedOfferTypes = signal<string[]>([]);
  showOfferTypeDropdown = signal(false);
  orderDir = signal<'ASC' | 'DESC'>('DESC');
  orderBy = signal<'TITLE' | 'START_DATE'>('TITLE');

  searchTerm = signal('');
  isDropDown = signal(false);
  results = signal<{ id: number; titleAr: string; titleEn: string }[]>([]);

  startedOnFrom = signal<string | null>(null);
  startedOnTo = signal<string | null>(null);
  endedOnFrom = signal<string | null>(null);
  endedOnTo = signal<string | null>(null);

  filters = input<OfferFiltersApi>();

  filtersChange = output<OfferFiltersApi>();

  onSearchChange(value: string) {
    this.searchTerm.set(value);
    this.isDropDown.set(true);
    if (!value) {
      this.isDropDown.set(false);
    }

    this.loading.set(true);
    this.errorSearching.set('');
    this.offersService
      .autoCompleteOffers(value)
      .pipe(
        finalize(() => {
          this.loading.set(false);
        }),
      )
      .subscribe({
        next: (results) => {
          console.log('Auto-complete results:', results);
          this.results.set(results.list || []);
        },
        error: (err) => {
          this.errorSearching.set(
            this.isArabic() ? err.messageAr || 'حدث خطأ أثناء البحث' : err.messageEn || 'Error fetching results',
          );
        },
      });
  }

  onSelectItem(item: { id: number; titleAr: string; titleEn: string }) {
    this.selectedItem.set(item);
  }

  // STATUS FILTER
  options = signal([
    { id: '1', titleAr: 'نشط', titleEn: 'Active' },
    { id: '2', titleAr: 'غير نشط', titleEn: 'Inactive' },
    { id: '3', titleAr: 'منتهي', titleEn: 'Expired' },
  ]);

  toggleStatusDropdown() {
    this.showStatusDropdown.update((v) => !v);
  }

  onStatusesChange(ids: string[]) {
    this.selectedStatuses.set(ids);
  }

  // OFFER TYPE FILTER
  offerTypes = signal([
    { id: '1', titleAr: 'خصم', titleEn: 'Discount' },
    { id: '2', titleAr: 'اشتر X واحصل على Y مجانا', titleEn: 'Buy X Get Y Free' },
  ]);

  toggleOfferTypeDropdown() {
    this.showOfferTypeDropdown.update((v) => !v);
  }

  onOfferTypesChange(ids: string[]) {
    this.selectedOfferTypes.set(ids);
  }

  filtersPayload = computed<OfferFiltersApi>(() => ({
    title: this.searchTerm(),
    status: this.selectedStatuses(),
    offerTypeIds: this.selectedOfferTypes(),

    startDateFrom: this.startedOnFrom()!,
    startDateTo: this.startedOnTo()!,

    endDateFrom: this.endedOnFrom()!,
    endDateTo: this.endedOnTo()!,

    orderBy: this.orderBy(),
    orderDir: this.orderDir(),
  }));

  onApplyFilters() {
    const payload = this.filtersPayload();
    console.log('Applying filters with payload:', this.searchTerm());
    console.log('Applying filters with payload:', payload);
    if (this.endedOnInvalid() || this.startedOnInvalid()) {
      return;
    }
    this.filtersChange.emit(payload);
  }

  toggleOrderDir() {
    const currentDir = this.filters()?.orderDir || 'DESC';
    const newDir = currentDir === 'DESC' ? 'ASC' : 'DESC';
    this.orderDir.set(newDir);
    this.onApplyFilters();
  }
  changeOrderBy(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const newOrderBy = selectElement.value as 'TITLE' | 'START_DATE';
    this.orderBy.set(newOrderBy);
  }

  onResetFilters() {
    this.searchTerm.set('');
    this.selectedStatuses.set([]);
    this.selectedOfferTypes.set([]);
    this.startedOnFrom.set(null);
    this.startedOnTo.set(null);
    this.endedOnFrom.set(null);
    this.endedOnTo.set(null);
    this.orderBy.set('TITLE');
    this.orderDir.set('DESC');
    this.onApplyFilters();
  }

  startedOnInvalid = computed(() => {
    const from = this.startedOnFrom();
    const to = this.startedOnTo();

    if (!from || !to) return false;

    return new Date(to) < new Date(from);
  });

  endedOnInvalid = computed(() => {
    const from = this.endedOnFrom();
    const to = this.endedOnTo();

    if (!from || !to) return false;

    return new Date(to) < new Date(from);
  });
}
